import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getVendorPaymentSummary,
} from "../../redux/actions/vendor";
import { toast } from "react-toastify";
import {
  HiOutlineCurrencyDollar,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineDownload,
} from "react-icons/hi";

const AdminVendorPayments = () => {
  const dispatch = useDispatch();
  const { paymentSummary, isLoading, error } = useSelector(
    (state) => state.vendor
  );
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(getVendorPaymentSummary(statusFilter, page));
  }, [dispatch, statusFilter, page]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch({ type: "clearVendorErrors" });
    }
  }, [error]);

  const payments = paymentSummary?.payments || [];
  const total = paymentSummary?.total || 0;
  const pages = paymentSummary?.pages || 1;
  const stats = paymentSummary?.stats || {};

  const paymentStatusBadge = (status) => {
    const colors = {
      not_ready: "bg-gray-100 text-gray-600",
      ready_for_payment: "bg-teal-100 text-teal-800",
      processing: "bg-blue-100 text-blue-800",
      paid: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-gray-100"}`}>
        {status?.replace(/_/g, " ")}
      </span>
    );
  };

  return (
    <div className="w-full p-4 md:p-6 min-h-screen bg-secondary-50">
      <div className="mb-8 pb-6 border-b border-secondary-200">
        <p className="text-xs uppercase tracking-[0.25em] text-accent-600 font-semibold mb-1">Admin</p>
        <h1 className="text-3xl font-bold text-black flex items-center gap-2">
          <HiOutlineCurrencyDollar className="w-8 h-8" />
          Vendor Payments
        </h1>
        <p className="text-secondary-500 text-sm mt-1">Track and manage vendor payment disbursements</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border-l-4 border-black shadow-sm p-4">
          <p className="text-xs text-secondary-500">Total Outstanding</p>
          <p className="text-xl font-bold text-black">₹{(stats.totalOutstanding || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white border-l-4 border-accent-500 shadow-sm p-4">
          <p className="text-xs text-secondary-500">Ready for Payment</p>
          <p className="text-xl font-bold text-black">₹{(stats.readyAmount || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white border-l-4 border-green-500 shadow-sm p-4">
          <p className="text-xs text-secondary-500">Total Paid</p>
          <p className="text-xl font-bold text-black">₹{(stats.totalPaid || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white border-l-4 border-secondary-400 shadow-sm p-4">
          <p className="text-xs text-secondary-500">Platform Earnings</p>
          <p className="text-xl font-bold text-black">₹{(stats.platformEarnings || 0).toFixed(2)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-1 mb-4">
        {["", "not_ready", "ready_for_payment", "processing", "paid", "failed"].map((s) => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
              statusFilter === s
                ? "bg-black text-white"
                : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
            }`}
          >
            {s?.replace(/_/g, " ") || "All"}
          </button>
        ))}
      </div>

      {/* Payment Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#111111]" />
        </div>
      ) : (
        <div className="bg-white border border-secondary-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary-50 border-b border-secondary-200">
                <tr>
                  {["Order ID", "Vendor", "Order Amount", "Commission", "Gateway Fee", "Net to Vendor", "Payment Status", "Paid Date"].map(
                    (h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {payments.length > 0 ? (
                  payments.map((p) => (
                    <tr key={p._id} className="hover:bg-secondary-50">
                      <td className="px-4 py-3 text-sm font-mono text-gray-700">
                        {p.order?._id?.slice(-8) || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {p.shop?.name || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        ₹{(p.vendorPaymentAmount || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-red-600">
                        -₹{(p.platformCommission || 0).toFixed(2)}
                        <span className="text-[10px] text-gray-400 ml-1">({p.platformCommissionRate}%)</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        ₹{(p.paymentGatewayFees || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-[#111111]">
                        ₹{(p.vendorNetAmount || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        {paymentStatusBadge(p.vendorPaymentStatus)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {p.paymentProcessedDate
                          ? new Date(p.paymentProcessedDate).toLocaleDateString("en-IN")
                          : "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-4 py-12 text-center text-gray-500">
                      No payment records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-t">
              <span className="text-sm text-gray-600">
                Page {page} of {pages} ({total} total)
              </span>
              <div className="flex gap-1">
                {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1 rounded text-sm ${page === p ? "bg-[#111111] text-white" : "bg-white text-gray-700 border hover:bg-gray-100"}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminVendorPayments;
